import { cn } from '@/lib/utils';
import React from 'react'
import { useTranslation } from 'react-i18next';
import { TruncatedText } from '@/components/common/truncated-text';
import { formatToFullDateTime } from '@/utils/date-formatter';
import { Pagination } from '@/components/common/pagination';
import { LoadingOverlay } from '@/components/common/loading-overlay';
import { AgentOperationLogData } from '@/services/agent-operation-logs-list';
import { CopyClipboard } from '../common/copy-clipboard';

interface TableListProps {
  operationLogs: AgentOperationLogData[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  changePage: (page: number) => void;
  search: () => void;
}

/** 操作日志列表 */
export const TableList = ({
  operationLogs,
  loading,
  total,
  page,
  pageSize,
  changePage,
  search,
}: TableListProps) => {
  const { t } = useTranslation();

  const handleSizeChange = (page: number) => {
    changePage(page);
    search();
  }

  const moduleTypeMap = {
    '1': t('agent_dashboard.operation_log.module_type_map.user'),
    '2': t('agent_dashboard.operation_log.module_type_map.order'),
    '3': t('agent_dashboard.operation_log.module_type_map.permission'),
    '4': t('agent_dashboard.operation_log.module_type_map.company_asset'),
    '5': t('agent_dashboard.operation_log.module_type_map.basic_settings'),
    '999': t('agent_dashboard.operation_log.module_type_map.other'),
  }

  const actionTypeMap = {
    '801': t('agent_dashboard.operation_log.action_type_map.create'),
    '802': t('agent_dashboard.operation_log.action_type_map.edit'),
    '803': t('agent_dashboard.operation_log.action_type_map.disable'),
    '804': t('agent_dashboard.operation_log.action_type_map.enable'),
    '805': t('agent_dashboard.operation_log.action_type_map.delete'),
    '999': t('agent_dashboard.operation_log.action_type_map.other'),
  }

  // 操作模块-操作类型映射
  const moduleActionMap = {
    '1': {
      '801': t('agent_dashboard.operation_log.action_map.user.create'),
      '802': t('agent_dashboard.operation_log.action_map.user.edit'),
      '803': t('agent_dashboard.operation_log.action_map.user.disable'),
      '804': t('agent_dashboard.operation_log.action_map.user.enable'),
      '805': t('agent_dashboard.operation_log.action_map.user.delete'),
      '806': t('agent_dashboard.operation_log.action_map.user.material_create'),
      '807': t('agent_dashboard.operation_log.action_map.user.material_delete'),
      '808': t('agent_dashboard.operation_log.action_map.user.knowledge_create'),
      '809': t('agent_dashboard.operation_log.action_map.user.knowledge_delete'),
    },
    '2': {
      '801': t('agent_dashboard.operation_log.action_map.order.create'),
    },
    '3': {
      '801': t('agent_dashboard.operation_log.action_map.permission.create'),
      '802': t('agent_dashboard.operation_log.action_map.permission.edit'),
      '803': t('agent_dashboard.operation_log.action_map.permission.disable'),
      '804': t('agent_dashboard.operation_log.action_map.permission.enable'),
      '805': t('agent_dashboard.operation_log.action_map.permission.delete'),
    },
    '4': {
      '801': t('agent_dashboard.operation_log.action_map.company_asset.create'),
      '803': t('agent_dashboard.operation_log.action_map.company_asset.disable'),
      '804': t('agent_dashboard.operation_log.action_map.company_asset.enable'),
      '805': t('agent_dashboard.operation_log.action_map.company_asset.delete'),
    },
    '5': {
      '802': t('agent_dashboard.operation_log.action_map.basic_settings.edit'),
    },
  }

  return (
    <div className='relative flex-1 flex flex-col rounded-lg border overflow-hidden'>
      <div
        className="overflow-auto flex-1"
      >
        <table className="w-full rounded-t-lg relative">
          <thead className='bg-gray-50 sticky top-0 border-b border-[rgba(234,236,240,1)] h-11 z-20'>
            <tr className="bg-gray-50">
              <Th>{t('agent_dashboard.operation_log.table.id')}</Th>
              <Th className='min-w-[7rem]'>
                {t('agent_dashboard.operation_log.table.name')}/
                {t('agent_dashboard.operation_log.table.user_id')}
              </Th>
              <Th>{t('agent_dashboard.operation_log.table.operation_module')}</Th>
              <Th>{t('agent_dashboard.operation_log.table.operation_type')}</Th>
              <Th>
                {t('agent_dashboard.operation_log.table.operation_object')}/
                {t('agent_dashboard.operation_log.table.operation_id')}
              </Th>
              <Th className='min-w-[7rem]'>{t('agent_dashboard.operation_log.table.operation_time')}</Th>
            </tr>
          </thead>
          <tbody className='bg-white'>
            {operationLogs?.length > 0 && operationLogs?.map((item, index) => (
              <tr key={`${item.id}_${index}`} className='border-b h-18 transition-colors duration-200'>
                <Td >
                  <TruncatedText text={item.operationId.toString() || item.id} className='text-[rgba(16,24,40,1)] pb-1' />
                </Td>
                <Td>
                  <TruncatedText text={item.username} className='text-[rgba(16,24,40,1)] pb-1' />
                  <div className='flex items-center gap-1'>
                    <TruncatedText text={item.userId.toString()} />
                    <CopyClipboard text={item.userId.toString()} />
                  </div>
                </Td>
                <Td>
                  <span className={cn(
                    'py-1 px-2 rounded-full font-medium text-sm',
                    item.operationModule === 1 && "text-[rgba(6,118,118,1)] bg-[rgba(238,254,255,1)]",
                    item.operationModule === 2 && "text-[rgba(181,71,8,1)] bg-[rgba(255,250,235,1)]",
                    item.operationModule === 3 && "text-[rgba(89,37,220,1)] bg-[rgba(244,243,255,1)]",
                    item.operationModule === 4 && "text-[rgba(193,21,116,1)] bg-[rgba(253,242,250,1)]",
                    item.operationModule === 5 && "text-[rgba(23,92,211,1)] bg-[rgba(239,248,255,1)]",
                    item.operationModule === 999 && "text-[rgba(16,24,40,1)] bg-[#f3f3f3]",
                  )}>
                    {moduleTypeMap[item.operationModule]}
                  </span>
                </Td>
                <Td>
                  {moduleActionMap[item.operationModule]?.[item.operationType] || actionTypeMap[item.operationType]}
                </Td>
                <Td>
                  <TruncatedText className='text-[rgba(16,24,40,1)] pb-1'  text={item.operationObjectName + `${item.operationModule === 4 ? t('agent_dashboard.operation_log.table.template') : ''}`} />
                  <div>
                    <TruncatedText text={item.operationObjectId} />
                  </div>
                </Td>
                <Td>{item.operationTime ? formatToFullDateTime(item.operationTime * 1000) : '-'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className='h-[4.25rem] border-t border-[rgba(234,236,240,1)] px-6 flex items-center'>
        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onChange={handleSizeChange}
        />
      </div>
      <LoadingOverlay loading={loading} />
    </div>
  )
}

const Th = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <th className={cn(
    'min-w-[6.25rem] max-w-[12.5rem] pl-6 text-left text-sm font-semibold text-[rgba(16,24,40,1)]',
    className,
  )}>
    {children}
  </th>
)

const Td = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <td className={cn(
    'min-w-[6.25rem] max-w-[12.5rem]  pl-6 text-sm font-medium  text-[rgba(71,84,103,1)]',
    className,
  )}>
    {children}
  </td>
)